import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Activity, Dna, RefreshCw, Stethoscope, Calendar } from "lucide-react";
import nudaLabsLogo from "@/assets/nudalabs-logo.png";
import NudaVitaeAssessmentModal from "./NudaVitaeAssessmentModal";

interface NudaLabsIntroProps {
  onOpenModal?: () => void;
  nudaLink?: string | null;
}

const NudaLabsIntro = ({ onOpenModal, nudaLink }: NudaLabsIntroProps) => {
  const [assessmentOpen, setAssessmentOpen] = useState(false);

  const handleNudaClick = () => {
    // Always capture lead first via the modal
    onOpenModal?.();
  };
  
  const features = [
    {
      icon: Activity,
      title: "Data first, not guesswork",
      description: "We start with biomarkers, lifestyle inputs, and goals to understand how your body is actually functioning.",
      gradient: "from-[#1B3A5F] via-[#2A4A6F] to-[#1B3A5F]",
    },
    {
      icon: Dna,
      title: "Personalized peptide formulation",
      description: "Your plan is designed around your biology—metabolism, recovery, inflammation, hormone signaling—not a one-size-fits-all protocol.",
      gradient: "from-[#1F3D5C] via-[#2C4E6E] to-[#1F3D5C]",
    },
    {
      icon: RefreshCw,
      title: "Adaptive over time",
      description: "As your body responds, your peptide plan evolves—doses, combinations, and timing adjust with you.",
      gradient: "from-[#1A3550] via-[#274560] to-[#1A3550]",
    },
    {
      icon: Stethoscope,
      title: "Clinically guided, human-led",
      description: "Backed by medical oversight and expert providers who interpret the data and guide next steps.",
      gradient: "from-[#1C3A58] via-[#2B4A68] to-[#1C3A58]",
    }
  ];

  return (
    <>
      <section id="nudalabs" className="py-20 md:py-28 lg:py-32 bg-gradient-to-br from-[#0D1B2A] via-[#1B263B] to-[#0D1B2A] scroll-mt-20 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="text-center space-y-6 mb-12 md:mb-16">
              {/* Headline */}
              <h2 className="text-3xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight">
                Let your body be the voice of{" "}
                <span className="text-[#ffdb4d]">your longevity plan.</span>
              </h2>
              
              {/* Powered by nudaLabs */}
              <div className="flex items-center justify-center gap-3">
                <span className="text-sm md:text-base text-white/60 font-medium">powered by</span>
                <img 
                  src={nudaLabsLogo} 
                  alt="nudaLabs" 
                  className="h-6 md:h-8 object-contain"
                />
              </div>
            </div>

            {/* Feature Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-12 md:mb-16">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className={`p-5 md:p-6 lg:p-8 rounded-xl md:rounded-2xl border border-white/10 
                    bg-gradient-to-br ${feature.gradient} 
                    shadow-lg hover:shadow-2xl hover:shadow-[#ffdb4d]/10
                    transition-all duration-500 ease-out 
                    hover:scale-[1.02] hover:border-[#ffdb4d]/30
                    group cursor-pointer relative overflow-hidden`}
                >
                  {/* Subtle glow effect on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-[#ffdb4d]/5 via-transparent to-[#ffdb4d]/5" />
                  
                  <div className="flex items-start gap-4 relative z-10">
                    <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center 
                      bg-white/10 group-hover:bg-[#ffdb4d]/20 
                      transition-all duration-300 group-hover:scale-110">
                      <feature.icon className="w-5 h-5 md:w-6 md:h-6 text-[#ffdb4d] transition-transform duration-300 group-hover:scale-110" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg md:text-xl font-bold mb-2 text-white group-hover:text-[#ffdb4d] transition-colors duration-300">
                        {feature.title}
                      </h3>
                      <p className="text-sm md:text-base leading-relaxed text-white/80 group-hover:text-white/90 transition-colors duration-300">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="text-center space-y-6">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto text-base md:text-lg px-6 md:px-8 py-5 md:py-6 h-auto font-bold group bg-[#ffdb4d] hover:bg-[#ffdb4d]/90 text-[#0D1B2A]"
                  onClick={handleNudaClick}
                >
                  Personalize my Plan
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto text-base md:text-lg px-6 md:px-8 py-5 md:py-6 h-auto font-bold group bg-white text-[#0D1B2A] hover:bg-white/90 border-2 border-white"
                  onClick={handleNudaClick}
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  See if you qualify
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <NudaVitaeAssessmentModal 
        open={assessmentOpen} 
        onOpenChange={setAssessmentOpen} 
      />
    </>
  );
};

export default NudaLabsIntro;
